import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsClearableMixin from '../../mixins/ids-clearable-mixin/ids-clearable-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsFormInputMixin from '../../mixins/ids-form-input-mixin/ids-form-input-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsMaskMixin from '../../mixins/ids-mask-mixin/ids-mask-mixin';
import IdsValidationMixin from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLoadingIndicatorMixin from '../../mixins/ids-loading-indicator-mixin/ids-loading-indicator-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsAutoComplete from './ids-autocomplete';
import IdsFieldIndicatorMixin from '../../mixins/ids-field-indicator-mixin/ids-field-indicator-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-icon/ids-icon';
import '../ids-text/ids-text';
import '../ids-trigger-field/ids-trigger-button';

import {
  TYPES,
  SIZES,
  TEXT_ALIGN,
  LABEL_WRAPS,
  MARGIN_BLOCK_END_SIZES,
  TypeValues,
  TypeKeys,
  SizeKeys,
  MarginBlockEndKey,
} from './ids-input-attributes';

import styles from './ids-input.scss';
import type IdsIcon from '../ids-icon/ids-icon';
import type IdsButton from '../ids-button/ids-button';
import type IdsText from '../ids-text/ids-text';
import { checkOverflow, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

const Base = IdsAutoSelectMixin(
  IdsTooltipMixin(
    IdsLabelStateMixin(
      IdsLoadingIndicatorMixin(
        IdsAutoComplete(
          IdsFieldHeightMixin(
            IdsDirtyTrackerMixin(
              IdsFieldIndicatorMixin(
                IdsClearableMixin(
                  IdsColorVariantMixin(
                    IdsMaskMixin(
                      IdsValidationMixin(
                        IdsLocaleMixin(
                          IdsKeyboardMixin(
                            IdsFormInputMixin(
                              IdsLabelPositionMixin(
                                IdsEventsMixin(
                                  IdsElement
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

// Setting defaults text-align
type IdsInputAlignment = 'start' | 'center' | 'end';

// Setting defaults label wrap
type IdsInputLabelWrap = 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch';

// Reveal password types
type RevealPasswordType = 'icon' | 'text' | boolean;

const REVEAL_PASSWORD_TYPES = {
  ICON: 'icon' as const,
  TEXT: 'text' as const,
  BOTH: true as const
} as const;

/**
 * IdsInput defines its template in a way that can be overridden by other
 * component types.
 */
export type IdsInputTemplateVariables = {
  ariaLabel: string;
  capsLock: string;
  containerClass: string;
  inputClass: string;
  inputState: string;
  labelHtml: string;
  placeholder: string;
  showHide: string;
  type: string;
  value: string;
};

/**
 * IDS Input Component
 * @type {IdsInput}
 * @inherits IdsElement
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @mixes IdsFormInputMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsClearableMixin
 * @mixes IdsColorVariantMixin
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsLabelStateMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsMaskMixin
 * @mixes IdsValidationMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsLoadingIndicatorMixin
 * @part container - the overall container
 * @part field-container - the container for the input
 * @part input - the input element
 * @part label - the label element
 */
@customElement('ids-input')
@scss(styles)
export default class IdsInput extends Base {
  triggeredByChange = false;

  constructor() {
    super();
  }

  isFormComponent = true;

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = [
    'alternate',
    'alternate-formatter',
    'borderless',
    'in-cell',
    'list-builder',
    'module-nav'
  ];

  /**
   * @returns {Array<string>} IdsInput component observable attributes
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.ACTIVE,
      attributes.AUTOSELECT,
      attributes.BG_TRANSPARENT,
      attributes.BROWSER_AUTOCOMPLETE,
      attributes.CAPS_LOCK,
      attributes.CURSOR,
      attributes.DISABLED,
      attributes.DISABLE_TOOLTIP,
      attributes.REVEAL_ON_EDIT,
      attributes.FORMAT,
      attributes.ID,
      attributes.LABEL_WRAP,
      attributes.LINE_CLAMP,
      attributes.LOWERCASE,
      attributes.MARGIN_BLOCK_END,
      attributes.MAXLENGTH,
      attributes.NO_MARGINS,
      attributes.OVERFLOW,
      attributes.PADDING,
      attributes.PASSWORD_VISIBLE,
      attributes.PLACEHOLDER,
      attributes.READONLY_BACKGROUND,
      attributes.READONLY,
      attributes.REVEALABLE_PASSWORD,
      attributes.SIZE,
      attributes.SQUARE,
      attributes.TABBABLE,
      attributes.TEXT_ALIGN,
      attributes.TEXT_ELLIPSIS,
      attributes.TYPE,
      attributes.UPPERCASE,
      attributes.VALUE,
    ];
  }

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();

    if (this.isPasswordVisible) {
      this.#togglePasswordEventSetUp(true);
    }

    if (this.capsLock) {
      this.#capsLockEventSetUp(true);
    }

    // Initialize tooltip based on label and labelWrap settings
    this.#updateLabelTooltip();

    if (this.lineClamp && this.labelTextEl) {
      this.labelTextEl.lineClamp = this.lineClamp;
    }

    if (this.labelWrap && this.labelTextEl) {
      this.labelTextEl.labelWrap = this.labelWrap;
    }

    this.#setReadonlyBackground();
    this.#passwordVisibilityHandler();

    if (this.getAttribute('maxlength')) {
      this.maxlength = this.getAttribute('maxlength');
    }

    if (getClosest(this, 'ids-header, .ids-header')) {
      this.colorVariant = 'alternate';
    }

    if (this.getAttribute('name')) {
      this.name = this.getAttribute('name') || '';
      this.input!.name = this.name;
    }
  }

  disconnectedCallback(): void {
    this.detachNativeEvents();
    super.disconnectedCallback();
    this.offEvent('click', this.labelEl);
    this.#labelEl = undefined;
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const {
      ariaLabel,
      capsLock,
      containerClass,
      inputClass,
      inputState,
      labelHtml,
      placeholder,
      showHide,
      type,
      value
    } = this.templateVariables();

    return `<div class="${containerClass}" part="container">
      ${labelHtml}
      <div class="field-container" part="field-container">
        <input
          part="input"
          ${type}${inputClass}${placeholder}${inputState}
          ${ariaLabel}
          ${value}
          ></input>
        ${showHide}
        ${capsLock}
        <slot name="loading-indicator"></slot>
      </div>
      ${this.autocomplete ? `
        <ids-popup
          type="dropdown"
          align="bottom, left"
          align-target="#${this.id}-input"
          part="popup"
        >
          <ids-list-box slot="content" size="${this.size}"></ids-list-box>
        </ids-popup>` : ''}
    </div>`;
  }

  /**
   * Uses current IdsInput state to generate strings used in its template.
   * @returns {IdsInputTemplateVariables} containing template strings used for generating an IdsInput template
   */
  templateVariables(): IdsInputTemplateVariables {
    const attrs = {
      readonly: this.readonly ? 'readonly' : '',
      disabled: this.disabled ? 'disabled' : '',
      required: this.validate ? 'required' : '',
      noMargins: this.noMargins ? 'no-margins' : '',
    };

    const placeholder = this.placeholder ? ` placeholder="${this.placeholder}"` : '';
    const type = ` type="${this.isPasswordVisible && this.passwordVisible ? 'text' : this.type || TYPES.default}"`;
    let inputClass = `ids-input-field ${this.textAlign}`;

    // Handle Password Fields
    const showHide = this.templateShowHide();
    const capsLock = this.templateCapsLock();

    inputClass += stringToBool(this.bgTransparent) ? ' bg-transparent' : '';
    inputClass += stringToBool(this.readonlyBackground) ? '' : ' readonly-background';
    inputClass += stringToBool(this.textEllipsis) ? ' text-ellipsis' : '';
    inputClass = ` class="${inputClass}"`;

    let inputState = stringToBool(this.readonly) ? ' readonly' : '';
    inputState = stringToBool(this.disabled) ? ' disabled' : inputState;

    let containerClass = `ids-input${inputState} ${this.size}`;
    containerClass += stringToBool(this.compact) ? ' compact' : '';
    containerClass += stringToBool(this.noMargins) ? ' no-margins' : '';

    const ariaLabel = this.hasAttribute(attributes.LABEL_STATE) && this.label ? `aria-label="${this.label}"` : '';
    const hiddenLabelCss = !this.label.length || this.labelState === 'hidden' ? ' empty' : '';
    const requiredLabelCss = !this.labelRequired ? ' no-required-indicator' : '';

    const labelHtml = `<label
      class="ids-label-text${requiredLabelCss}${hiddenLabelCss}"
      part="label"
      ${attrs.readonly}
      ${attrs.disabled}
      ${attrs.required}
    >
      <slot name="label-pre"></slot>
      <ids-text part="label" color-unset>
        ${this.label}
      </ids-text>
      <slot name="label-post"></slot>
    </label>`;

    const value = this.hasAttribute(attributes.VALUE) ? ` value="${this.getAttribute(attributes.VALUE)}" ` : '';

    return {
      ariaLabel,
      capsLock,
      containerClass,
      inputClass,
      inputState,
      labelHtml,
      placeholder,
      showHide,
      type,
      value
    };
  }

  /**
   * Check if this is a password field and the password should be shown as plain text
   * @returns {boolean | string} true if password is visible
   */
  get isPasswordVisible(): boolean | string {
    return this.revealablePassword && this.type === TYPES.password;
  }

  /**
   * Get the collapsible state
   * @returns {boolean} true if collapsible
   */
  get collapsible(): boolean {
    return this.hasAttribute(attributes.COLLAPSIBLE) || false;
  }

  templateShowHide(): string {
    if (!this.isPasswordVisible) return '';
    const showText = this.localeAPI?.translate('Show')?.toUpperCase();
    const hideText = this.localeAPI?.translate('Hide')?.toUpperCase();
    const showIcon = 'show-item';
    const hideIcon = 'hide-item';
    const isVisible = this.passwordVisible;
    const isCompact = this.compact;
    const height = isCompact ? '' : this.getShowHideButtonHeight(this.fieldHeight);

    if (this.revealablePassword === REVEAL_PASSWORD_TYPES.ICON) {
      return `<ids-button id="show-hide-password" class="show-hide-password icon-only" ${isCompact ? 'compact' : ''} ${height ? `${height.name}="${height.val}"` : ''} no-padding icon="${isVisible ? hideIcon : showIcon}"></ids-button>`;
    }
    if (this.revealablePassword === REVEAL_PASSWORD_TYPES.TEXT) {
      return `<ids-button id="show-hide-password" class="show-hide-password" no-padding ${isCompact ? 'compact' : ''} ${height ? `${height.name}="${height.val}"` : ''} text="${isVisible ? hideText : showText}"></ids-button>`;
    }
    return `<ids-button id="show-hide-password" class="show-hide-password" no-padding text="${isVisible ? hideText : showText}" icon="${isVisible ? hideIcon : showIcon}" ${isCompact ? 'compact' : ''} ${height ? `${height.name}="${height.val}"` : ''}></ids-button>`;
  }

  templateCapsLock(): string {
    return this.capsLock
      ? `<ids-icon id="caps-lock-indicator" class="caps-lock-indicator" icon="capslock"></ids-icon>`
      : '';
  }

  onColorVariantRefresh(value: string): void {
    super.colorVariant = value;
    if (this.clearable) {
      this.refreshClearableButtonStyles();
    }
  }

  /**
   * Get the inner input element
   * @returns {HTMLInputElement | null} the input element
   */
  get input(): HTMLInputElement | null {
    return this.container?.querySelector<HTMLInputElement>(`input[part="input"]`) ?? null;
  }

  /**
   * Get the inner input element (for form input mixin)
   * @returns {HTMLInputElement | null} the input element
   */
  get formInput(): HTMLInputElement | null {
    return this.input;
  }

  /**
   * Get the caps lock indicator icon
   * @returns {HTMLElement | undefined | null} the caps lock icon
   */
  get capsLockIcon(): HTMLElement | undefined | null {
    return this.container?.querySelector<IdsIcon>('#caps-lock-indicator');
  }

  /**
   * Get the field container element
   * @returns {HTMLElement | undefined | null} the field container
   */
  get fieldContainer(): HTMLElement | undefined | null {
    return this.container?.querySelector('.field-container');
  }

  /**
   * Get the inner ids-text element which contains the label text
   * @returns {IdsText | null} the label text element
   */
  get labelTextEl(): IdsText | null {
    return this.shadowRoot?.querySelector<IdsText>('label ids-text') ?? null;
  }

  /**
   * Get the inner label element
   * @returns {HTMLLabelElement | undefined | null} the label element
   */
  get labelEl(): HTMLLabelElement | undefined | null {
    return this.#labelEl || this.shadowRoot?.querySelector<HTMLLabelElement>('label');
  }

  /**
   * Get whether edit reveal is enabled
   * @returns {boolean | string} true if edit reveal is enabled
   */
  get revealOnEdit(): boolean {
    return this.hasAttribute(attributes.REVEAL_ON_EDIT);
  }

  /**
   * Sets whether reveal on edit is enabled (shows button only when editing)
   * @param {boolean | string | null} value boolean value sets whether reveal on edit is enabled
   */
  set revealOnEdit(value: boolean | string | null) {
    this.toggleAttribute(attributes.REVEAL_ON_EDIT, stringToBool(value));
    if (this.isPasswordVisible) {
      this.#togglePasswordEventSetUp(this.revealablePassword);
    }
  }

  /**
   * Get whether password reveal functionality is enabled
   * @returns {RevealPasswordType} 'icon', 'text', or true if password reveal is enabled
   */
  get revealablePassword(): RevealPasswordType {
    const value = this.getAttribute(attributes.REVEALABLE_PASSWORD);
    if (value === REVEAL_PASSWORD_TYPES.ICON || value === REVEAL_PASSWORD_TYPES.TEXT) return value;
    return stringToBool(value);
  }

  /**
   * sets whether password reveal functionality is available
   * @param {RevealPasswordType} value 'icon', 'text', or true
   */
  set revealablePassword(value: RevealPasswordType) {
    if (this.type !== TYPES.password) {
      this.removeAttribute(attributes.REVEALABLE_PASSWORD);
      return;
    }

    const existingButton = this.container?.querySelector('.show-hide-password');

    if (value === REVEAL_PASSWORD_TYPES.ICON || value === REVEAL_PASSWORD_TYPES.TEXT) {
      this.setAttribute(attributes.REVEALABLE_PASSWORD, value);
      if (existingButton) {
        this.#togglePasswordEventSetUp(false);
      }
      this.#togglePasswordEventSetUp(value);
    } else if (stringToBool(value)) {
      this.setAttribute(attributes.REVEALABLE_PASSWORD, 'true');
      if (existingButton) {
        this.#togglePasswordEventSetUp(false);
      }
      this.#togglePasswordEventSetUp(true);
    } else {
      this.setAttribute(attributes.REVEALABLE_PASSWORD, 'false');
      this.#togglePasswordEventSetUp(false);
    }
  }

  /**
   * Get whether the capslock indicator is enabled
   * @returns {boolean | string} true if caps lock indicator is enabled
   */
  get capsLock(): boolean | string {
    return stringToBool(this.getAttribute(attributes.CAPS_LOCK));
  }

  /**
   * sets whether capslock indicatoris enabled or disabled
   * @param {boolean | string} value sets whether capslock indicator functionality is toggled on or off
   */
  set capsLock(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.CAPS_LOCK, 'true');
      this.#capsLockEventSetUp(true);
    } else {
      this.removeAttribute(attributes.CAPS_LOCK);
      this.#capsLockEventSetUp(false);
    }
  }

  /**
   * Get whether the password is currently visible
   * @returns {boolean} true if password is visible
   */
  get passwordVisible(): boolean {
    return stringToBool(this.getAttribute(attributes.PASSWORD_VISIBLE));
  }

  /**
   * sets whether the password is currently visible
   * @param {boolean} value toggles the visibility of the password on or off
   */
  set passwordVisible(value: boolean) {
    const valueSafe = stringToBool(value);
    if (valueSafe !== this.passwordVisible) {
      if (valueSafe) {
        this.setAttribute(attributes.PASSWORD_VISIBLE, 'true');
      } else {
        this.setAttribute(attributes.PASSWORD_VISIBLE, 'false');
      }
    }

    this.#passwordVisibilityHandler();
  }

  /**
   * Sets the id internally and externally
   * @param {string} value id value
   */
  set id(value: string) {
    const shadowId = `${value}-internal`;
    this.setAttribute(attributes.ID, value);
    this.shadowRoot?.querySelector('label')?.setAttribute('for', shadowId);
    this.shadowRoot?.querySelector('input')?.setAttribute('id', shadowId);
  }

  /**
   * Get the id
   * @returns {string} the id (default 'none')
   */
  get id(): string {
    return this.getAttribute(attributes.ID) || 'none';
  }

  /**
   * Sets whether the input field has square corners
   * @param {boolean} val If true will set `square` attribute and add square styling
   */
  set square(val: boolean) {
    const isSquare = stringToBool(val);
    this.toggleAttribute(attributes.SQUARE, isSquare);
    this.container?.querySelector('.field-container')?.classList.toggle('square', isSquare);
  }

  /**
   * Get whether the input has square corners
   * @returns {boolean} true if square
   */
  get square(): boolean {
    return stringToBool(this.getAttribute(attributes.SQUARE));
  }

  /**
   * Sets whether to disable tooltip
   * @param {boolean | string} val boolean flag
   */
  set disableTooltip(val: boolean | string) {
    this.toggleAttribute(attributes.DISABLE_TOOLTIP, stringToBool(val));
  }

  /**
   * Get whether tooltip is disabled
   * @returns {boolean | string} true if tooltip is disabled
   */
  get disableTooltip(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLE_TOOLTIP));
  }

  /**
   * Set input state for disabled or readonly
   * @private
   * @param {string} prop The property.
   * @returns {void}
   */
  setInputState(prop: string): void {
    if (prop === attributes.READONLY || prop === attributes.DISABLED) {
      if (!this.shadowRoot) return;

      const msgNodes = [].slice.call(this.shadowRoot.querySelectorAll('.validation-message'));
      const options = {
        prop1: prop,
        prop2: prop !== attributes.READONLY ? attributes.READONLY : attributes.DISABLED,
        val: stringToBool((this as any)[prop])
      };

      if (options.val) {
        this.input?.removeAttribute(options.prop2);
        this.container?.classList?.remove?.(options.prop2);
        msgNodes.forEach((x: any) => x.classList.remove(options.prop2));

        this.input?.setAttribute(options.prop1, 'true');
        this.container?.classList.add(options.prop1);
        msgNodes.forEach((x: any) => x.classList.add(options.prop1));
      } else {
        this.input?.removeAttribute(options.prop1);
        this.container?.classList.remove(options.prop1);
        msgNodes.forEach((x: any) => x.classList.remove(options.prop1));
      }
    }
  }

  /**
   * Set the label text
   * @private
   * @param {string} value of label
   * @returns {void}
   */
  setLabelText(value: string): void {
    return super.setLabelText(value, `label`);
  }

  /**
   * handles teardown and set up for capslock detection events
   * @param {boolean | string} value indicates whether to turn events on or off
   */
  #capsLockEventSetUp(value: boolean | string) {
    const updateCapsLockIcon = (e: any) => {
      if (this.capsLockIcon && e.getModifierState) {
        this.capsLockIcon.hidden = !e.getModifierState('CapsLock');
      }
    };

    if (value) {
      if (!this.capsLockIcon && this.input) {
        this.input.insertAdjacentHTML('afterend', this.templateCapsLock());
      }
      this.offEvent('keydown.input-capslock');
      this.onEvent('keydown.input-capslock', this, updateCapsLockIcon);
      this.offEvent('keyup.input-capslock');
      this.onEvent('keyup.input-capslock', this, updateCapsLockIcon);
      if (this.capsLockIcon) {
        this.capsLockIcon.hidden = true;
      }
    } else {
      this.offEvent('keydown.input-capslock');
      this.offEvent('keyup.input-capslock');
      this.capsLockIcon?.remove();
    }
  }

  #nativeEventHandler = (e: any) => {
    this.triggerEvent(e.type, this, {
      detail: {
        elem: this,
        nativeEvent: e,
        value: this.value,
        origin: 'input'
      }
    });
  };

  #inputEventHandler = (evt: any) => {
    if (evt instanceof InputEvent) {
      evt.stopPropagation();
    }
  };

  /**
   * Setup event handlers that trigger on the host element during native events from the internal HTMLInputElement
   * @private
   * @returns {this} The IdsInput API for chaining
   */
  #attachNativeEvents(): this {
    if (!this.input) {
      return this;
    }

    const events = ['focus', 'select', 'keydown', 'keypress', 'keyup', 'click', 'dbclick'];
    events.forEach((evt) => {
      this.input?.removeEventListener(evt, this.#nativeEventHandler);
      this.input?.addEventListener(evt, this.#nativeEventHandler);
    });

    // suppress native input event
    this.input.removeEventListener('input', this.#inputEventHandler);
    this.input.addEventListener('input', this.#inputEventHandler);

    return this;
  }

  /**
   * Removes all native event listeners from the input element.
   * Detaches focus, select, keyboard, click, and input event handlers that were
   * previously attached to the internal HTMLInputElement. This method is typically
   * called during component cleanup to prevent memory leaks.
   * @returns {void}
   */
  detachNativeEvents() {
    if (!this.input) return;
    const events = ['focus', 'select', 'keydown', 'keypress', 'keyup', 'click', 'dbclick'];
    events.forEach((evt) => {
      this.input?.removeEventListener(evt, this.#nativeEventHandler);
    });
    this.input.removeEventListener('input', this.#inputEventHandler);
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.#attachNativeEvents();
    this.onEvent('click', this.labelEl, () => {
      this.focus();
    });

    /**
     * Update tooltip on input change
     */
    this.onEvent('input', this?.input, () => {
      if (this.onBeforeInput()) {
        return;
      }
      this.#updateValue();
      this.triggerEvent('combochange', this, { detail: { elem: this, value: this.value } });
    });

    this.onLocaleChange = () => {
      this.#refreshContent();
      this.setInputState('disabled');
      this.setInputState('readonly');
      this.setLabelText(this.label);
      this.#capsLockEventSetUp(this.capsLock);
      this.#togglePasswordEventSetUp(this.revealablePassword);
      this.#setReadonlyBackground();
    };
  }

  #updateValue(): void {
    if (this?.getAttribute(attributes.VALUE) !== this.input?.value) {
      this.setAttribute(attributes.VALUE, this.input?.value || '');
    }
  }

  /**
   * Updates the tooltip attribute based on the input's value and overflow state.
   * Only updates if tooltip is 'true' (default overflow mode), custom tooltip strings are preserved.
   */
  #updateTooltip(): void {
    if (this.overflow !== 'ellipsis') return;
    if (this.tooltip && this.tooltip !== 'true') return;
    if (this.type !== TYPES.password) {
      this.setAttribute('tooltip', this.input?.value || '');
    } else {
      this.removeAttribute('tooltip');
    }
  }

  /**
   * handles event set up and teardown for password indicator
   * @private
   * @param {RevealPasswordType} value whether to toggle events on or off
   */
  #togglePasswordEventSetUp(value: RevealPasswordType) {
    let showHidePasswordElem = this.container?.querySelector('.show-hide-password');

    if (value) {
      if (!showHidePasswordElem && this.isPasswordVisible) {
        const showHideButton: any = document.createElement('ids-button');
        const showText = this.localeAPI?.translate('Show')?.toUpperCase();
        const hideText = this.localeAPI?.translate('Hide')?.toUpperCase();
        const showIcon = 'show-item';
        const hideIcon = 'hide-item';
        const isVisible = this.passwordVisible;
        const isCompact = this.compact;
        const fieldHeight = this.fieldHeight;

        if (value === REVEAL_PASSWORD_TYPES.ICON) {
          showHideButton.icon = isVisible ? hideIcon : showIcon;
          showHideButton.classList.add('icon-only');
        } else if (value === REVEAL_PASSWORD_TYPES.TEXT) {
          showHideButton.text = isVisible ? hideText : showText;
        } else {
          showHideButton.text = isVisible ? hideText : showText;
          showHideButton.icon = isVisible ? hideIcon : showIcon;
        }

        showHideButton.id = 'show-hide-password';
        showHideButton.classList.add('show-hide-password');
        showHideButton.noPadding = true;
        showHideButton.compact = isCompact;
        showHideButton.fieldHeight = fieldHeight;

        if (this.input) {
          this.input.insertAdjacentElement('afterend', showHideButton);
          this.input.type = `${this.passwordVisible ? 'text' : this.type}`;
        }
        showHidePasswordElem = this.container?.querySelector('.show-hide-password');
      }
      this.offEvent('click.input-showhidepassword');
      this.onEvent('click.input-showhidepassword', showHidePasswordElem, (e: MouseEvent) => {
        e.preventDefault();
        this.passwordVisible = !this.passwordVisible;
        this.#passwordVisibilityHandler();
        this.input?.focus();
      });

      if (this.revealOnEdit) {
        showHidePasswordElem?.setAttribute(attributes.HIDDEN, '');
        this.offEvent('input.input-revealedit');
        this.onEvent('input.input-revealedit', this.input, () => {
          showHidePasswordElem?.removeAttribute(attributes.HIDDEN);
          this.#passwordVisibilityHandler();
        });
        this.offEvent('blur.input-revealedit');
        this.onEvent('blur.input-revealedit', this.input, (e: FocusEvent) => {
          if (e.relatedTarget !== showHidePasswordElem && !showHidePasswordElem?.contains(e.relatedTarget as Node)) {
            showHidePasswordElem?.setAttribute(attributes.HIDDEN, '');
            if (this.passwordVisible) {
              this.passwordVisible = false;
              this.#passwordVisibilityHandler();
            }
          }
        });
      } else {
        this.offEvent('input.input-revealedit');
        this.offEvent('blur.input-revealedit');
        showHidePasswordElem?.removeAttribute(attributes.HIDDEN);
        this.#passwordVisibilityHandler();
      }
    } else {
      this.offEvent('click.input-showhidepassword');
      this.offEvent('input.input-revealedit');
      this.offEvent('blur.input-revealedit');
      this.input?.setAttribute('type', this.type);
      showHidePasswordElem?.remove();
    }
  }

  /**
   * toggles the visibility of the password by changing field type
   * @private
   */
  #passwordVisibilityHandler(): void {
    if (!this.shadowRoot) return;
    const passwordButton = this.shadowRoot.querySelector<IdsButton>(`.show-hide-password`);
    const passwordField = this.shadowRoot.querySelector(`.ids-input-field`);
    const hasPasswordVisibleAttr = this.getAttribute(attributes.PASSWORD_VISIBLE);

    if (hasPasswordVisibleAttr !== null) {
      const showText = this.localeAPI?.translate('Show')?.toUpperCase();
      const hideText = this.localeAPI?.translate('Hide')?.toUpperCase();
      const showIcon = 'show-item';
      const hideIcon = 'hide-item';
      const isVisible = this.passwordVisible;

      if (this.revealablePassword === REVEAL_PASSWORD_TYPES.ICON) {
        passwordButton?.setAttribute(attributes.ICON, isVisible ? hideIcon : showIcon);
        passwordButton?.removeAttribute(attributes.TEXT);
      } else if (this.revealablePassword === REVEAL_PASSWORD_TYPES.TEXT) {
        passwordButton?.setAttribute(attributes.TEXT, isVisible ? hideText : showText);
        passwordButton?.removeAttribute(attributes.ICON);
      } else {
        passwordButton?.setAttribute(attributes.TEXT, isVisible ? hideText : showText);
        passwordButton?.setAttribute(attributes.ICON, isVisible ? hideIcon : showIcon);
      }
      passwordField?.setAttribute(attributes.TYPE, isVisible ? 'text' : 'password');
    }
  }

  #refreshContent(): void {
    if (!this.shadowRoot) return;
    const passwordButton = this.shadowRoot.querySelector<IdsButton>(`.show-hide-password`);
    if (passwordButton) {
      const showText = this.localeAPI?.translate('Show')?.toUpperCase();
      const hideText = this.localeAPI?.translate('Hide')?.toUpperCase();
      const isVisible = this.passwordVisible;

      if (this.revealablePassword === REVEAL_PASSWORD_TYPES.TEXT || this.revealablePassword === REVEAL_PASSWORD_TYPES.BOTH) {
        passwordButton.setAttribute(attributes.TEXT, isVisible ? hideText : showText);
      }
    }
  }

  /**
   * When set the input will add a CSS class `is-active` that simulates the text input being "focused".
   * @param {boolean | string} value If true will set `is-active` attribute
   */
  set active(value: boolean | string) {
    const val = stringToBool(value);
    const className = 'is-active';
    if (val) {
      this.setAttribute(attributes.ACTIVE, val.toString());
      this.container?.classList.add(className);
    } else {
      this.removeAttribute(attributes.ACTIVE);
      this.container?.classList.remove(className);
    }
  }

  /**
   * Get the active state
   * @returns {boolean | string} true if active
   */
  get active(): boolean | string { return stringToBool(this.hasAttribute(attributes.ACTIVE)); }

  /**
   * When set the input will add css class `bg-transparent`
   * @param {boolean | string} value If true will set `bg-transparent` attribute
   */
  set bgTransparent(value: boolean | string) {
    const val = stringToBool(value);
    const className = 'bg-transparent';

    if (val && !this.readonly) {
      this.setAttribute(attributes.BG_TRANSPARENT, val.toString());
      this.container?.classList.add(className);
      this.input?.classList.add(className);
    } else {
      this.removeAttribute(attributes.BG_TRANSPARENT);
      this.container?.classList.remove(className);
      this.input?.classList.remove(className);
    }
  }

  /**
   * Get the background transparent state
   * @returns {boolean | string} true if background is transparent
   */
  get bgTransparent(): boolean | string { return stringToBool(this.getAttribute(attributes.BG_TRANSPARENT)); }

  /**
   * When set the input will add `autocomplete` attribute to the input for browser autocomplete.
   * There is an existing autocomplete setting that makes the input have a autcomplete using the autcomplete API.
   * @param {string} value to use for the autocomplete attribute
   */
  set browserAutocomplete(value: string) {
    if (value) {
      this.setAttribute(attributes.BROWSER_AUTOCOMPLETE, value);
      this.input?.setAttribute(attributes.AUTOCOMPLETE, value);
    } else {
      this.removeAttribute(attributes.BROWSER_AUTOCOMPLETE);
      this.input?.removeAttribute(attributes.AUTOCOMPLETE);
    }
  }

  /**
   * Get the browser autocomplete setting
   * @returns {string} the autocomplete value
   */
  get browserAutocomplete(): string { return String(this.getAttribute(attributes.BROWSER_AUTOCOMPLETE)); }

  /**
   * When set the input will add css class `text-ellipsis`
   * @param {boolean | string} value If true will set `text-ellipsis` attribute
   */
  set textEllipsis(value: boolean | string) {
    const val = stringToBool(value);
    const className = 'text-ellipsis';
    if (val) {
      this.setAttribute(attributes.TEXT_ELLIPSIS, val.toString());
      this.input?.classList.add(className);
    } else {
      this.removeAttribute(attributes.TEXT_ELLIPSIS);
      this.input?.classList.remove(className);
    }
  }

  /**
   * Get the text ellipsis state
   * @returns {boolean | string} true if text ellipsis is enabled
   */
  get textEllipsis(): boolean | string { return stringToBool(this.getAttribute(attributes.TEXT_ELLIPSIS)); }

  /**
   * Sets input to disabled
   * @param {boolean | string} value If true will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.DISABLED, 'true');
      // disabled and readonly are mutually exclusive — remove readonly when disabling
      if (this.hasAttribute(attributes.READONLY)) {
        this.removeAttribute(attributes.READONLY);
        this.setInputState(attributes.READONLY);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
    this.setInputState(attributes.DISABLED);
    if (this.handleClearable) this.handleClearable();
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string { return stringToBool(this.getAttribute(attributes.DISABLED)); }

  /**
   * internal reference to a label element a user provides
   */
  #labelEl?: HTMLLabelElement;

  /**
   * Set the `placeholder` of input
   * @param {string} value of the `placeholder` property
   */
  set placeholder(value: string | null) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      this.input?.setAttribute(attributes.PLACEHOLDER, value);
      return;
    }
    this.removeAttribute(attributes.PLACEHOLDER);
    this.input?.removeAttribute(attributes.PLACEHOLDER);
  }

  /**
   * Get the placeholder text
   * @returns {string | null} the placeholder text
   */
  get placeholder(): string | null {
    return this.getAttribute(attributes.PLACEHOLDER);
  }

  /**
   * Set the input to readonly state
   * @param {boolean | string} value If true will set `readonly` attribute
   */
  set readonly(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.READONLY, val.toString());
      // disabled and readonly are mutually exclusive — remove disabled when setting readonly
      if (this.hasAttribute(attributes.DISABLED)) {
        this.removeAttribute(attributes.DISABLED);
        this.setInputState(attributes.DISABLED);
      }
    } else {
      this.removeAttribute(attributes.READONLY);
    }
    this.setInputState(attributes.READONLY);
    if (this.handleClearable) this.handleClearable();
  }

  /**
   * Get the readonly state
   * @returns {boolean | string} true if readonly
   */
  get readonly(): boolean | string { return stringToBool(this.getAttribute(attributes.READONLY)); }

  /**
   * @param {boolean | string} value If true, causes an IdsInput set to `readonly` to appear
   * to use its standard field background color instead of the "readonly" state color
   */
  set readonlyBackground(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.READONLY_BACKGROUND, val.toString());
    } else {
      this.removeAttribute(attributes.READONLY_BACKGROUND);
    }

    this.#setReadonlyBackground();
  }

  /**
   * Get whether readonly background is enabled
   * @returns {boolean | string} true if readonly background is enabled
   */
  get readonlyBackground(): boolean | string {
    return stringToBool(this.getAttribute(attributes.READONLY_BACKGROUND));
  }

  #setReadonlyBackground() {
    this.container?.classList.toggle('readonly-background', Boolean(this.readonlyBackground));
  }

  /**
   * Update tooltip on label text element based on current label and labelWrap settings
   * @private
   */
  #updateLabelTooltip() {
    if (this.labelTextEl) {
      if ((this.labelWrap?.includes('ellipsis') || this.lineClamp) && this.label) {
        this.labelTextEl.setAttribute(attributes.TOOLTIP, this.label);
      } else {
        this.labelTextEl.removeAttribute('tooltip');
      }
    }
  }

  /**
   * Set the label wrap setting of input
   * @param {IdsInputLabelWrap} value ['ellipsis', 'wrap', 'no-stretch-ellipsis', 'no-stretch-wrap']
   */
  set labelWrap(value: IdsInputLabelWrap) {
    if (value && LABEL_WRAPS.includes(value)) {
      this.setAttribute(attributes.LABEL_WRAP, value);
    } else {
      this.removeAttribute(attributes.LABEL_WRAP);
    }

    if (this.labelTextEl) {
      this.labelTextEl.labelWrap = value;
    }

    this.#updateLabelTooltip();
  }

  /**
   * Get the label wrap setting
   * @returns {IdsInputLabelWrap} the label wrap setting (default 'wrap')
   */
  get labelWrap(): IdsInputLabelWrap {
    const value = this.getAttribute(attributes.LABEL_WRAP) as IdsInputLabelWrap;
    // Return default if value is not in valid LABEL_WRAPS array
    if (value && LABEL_WRAPS.includes(value)) {
      return value;
    }
    return 'wrap';
  }

  /**
   * Set the line clamp value for the label text
   * @param {string | number | null} value The number of lines to clamp
   */
  set lineClamp(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.LINE_CLAMP, `${value}`);
    } else {
      this.removeAttribute(attributes.LINE_CLAMP);
    }

    if (this.labelTextEl) {
      this.labelTextEl.lineClamp = value;
    }

    this.#updateLabelTooltip();
  }

  /**
   * Get the line clamp value
   * @returns {string | null} the line clamp value
   */
  get lineClamp(): string | null {
    return this.getAttribute(attributes.LINE_CLAMP);
  }

  /**
   * Set the size (width) of input
   * @param {string} value [xs, sm, mm, md, lg, full]
   */
  set size(value: string) {
    const size = SIZES[value as SizeKeys] || SIZES.default;
    this.setAttribute(attributes.SIZE, size);
    this.container?.classList.remove(...Object.values(SIZES));
    this.container?.classList.add(size);

    this.querySelector('ids-trigger-button')?.classList.remove(...Object.values(SIZES));
    this.querySelector('ids-trigger-button')?.classList.add(size);
  }

  /**
   * Get the size
   * @returns {string} the size
   */
  get size(): string {
    return this.getAttribute(attributes.SIZE) || SIZES.default;
  }

  /**
   * Sets the text alignment
   * @param {IdsInputAlignment} value [start, center, end]
   */
  set textAlign(value: IdsInputAlignment) {
    const textAlign = TEXT_ALIGN[value] || TEXT_ALIGN.default;
    this.setAttribute(attributes.TEXT_ALIGN, textAlign);
    this.input?.classList.remove('start', 'center', 'end');
    this.input?.classList.add(textAlign);
  }

  /**
   * Get the text alignment
   * @returns {IdsInputAlignment} the text alignment
   */
  get textAlign(): IdsInputAlignment {
    return this.getAttribute(attributes.TEXT_ALIGN) as IdsInputAlignment || TEXT_ALIGN.default;
  }

  /**
   * Sets the input type
   * @param {string} value [text, password, number, phone, email]
   */
  set type(value: string) {
    const type = TYPES[value as TypeKeys];
    if (type) {
      if ((type === TYPES.password || type === TYPES.text) && !this.isPasswordVisible) {
        this.setAttribute(attributes.TYPE, value);
        this.input?.setAttribute(attributes.TYPE, type);
      } else if (type !== TYPES.password && type !== TYPES.text) {
        this.setAttribute(attributes.TYPE, value);
        this.input?.setAttribute(attributes.TYPE, type);
      }
      return;
    }
    this.setAttribute(attributes.TYPE, TYPES.default);
    this.input?.setAttribute(attributes.TYPE, TYPES.default);
  }

  /**
   * Get the input type
   * @returns {string} the input type
   */
  get type(): string {
    return this.getAttribute(attributes.TYPE) as TypeValues ?? TYPES.default;
  }

  /**
   * Set the `value` attribute of input
   * @param {string} val the value property
   */
  set value(val: string | undefined | null) {
    let v = ['string', 'number'].includes(typeof val) ? String(val) : String(val || '');
    const currentValue = this.value;

    // If a mask is enabled, use the conformed value.
    // If no masking occurs, simply use the provided value.
    if (this.mask) {
      v = this.processMaskFromProperty(val || '') || v;
    }

    if (this.input) {
      this.input.value = v;
    }

    if (currentValue !== v) {
      this.setAttribute(attributes.VALUE, v);
    }
  }

  /**
   * Get the input value
   * @returns {string} the input value
   */
  get value(): string {
    return this.input?.value ?? '';
  }

  /**
   * Set the css cursor property to something other than text
   * @param {string} value the css cursor value
   */
  set cursor(value: string) {
    this.setAttribute(attributes.CURSOR, value);
    this.input?.style.setProperty('cursor', value);
  }

  /**
   * Get the cursor style
   * @returns {string} the cursor style
   */
  get cursor(): string {
    return this.getAttribute(attributes.CURSOR) ?? '';
  }

  /**
   * Sets the max width when typing
   * @param {number | string | null} n The max length to use
   */
  set maxlength(n: number | string | null) {
    // Convert string to number if possible
    const numValue = typeof n === 'string' ? parseInt(n, 10) : n;

    // Check if value is null or NaN
    if (numValue === null || Number.isNaN(numValue)) {
      this.removeAttribute(attributes.MAXLENGTH);
      this.input?.removeAttribute(attributes.MAXLENGTH);
      return;
    }

    // Handle zero and positive numbers
    if (numValue >= 0) {
      this.setAttribute(attributes.MAXLENGTH, numValue.toString());
      this.input?.setAttribute(attributes.MAXLENGTH, numValue.toString());
      return;
    }

    // Remove attribute for negative numbers
    this.removeAttribute(attributes.MAXLENGTH);
    this.input?.removeAttribute(attributes.MAXLENGTH);
  }

  /**
   * Get the max length
   * @returns {number} the max length
   */
  get maxlength(): number {
    return Number(this.getAttribute(attributes.MAXLENGTH));
  }

  /**
   * Sets the text to all upper case
   * @param {boolean | string} n If true use upper case
   */
  set uppercase(n: boolean | string) {
    if (stringToBool(n)) {
      this.setAttribute(attributes.UPPERCASE, '');
      this.input?.classList.add('is-uppercase');
      return;
    }
    this.removeAttribute(attributes.UPPERCASE);
    this.input?.classList.remove('is-uppercase');
  }

  /**
   * Get the uppercase state
   * @returns {boolean | string} true if uppercase is enabled
   */
  get uppercase(): boolean | string {
    return stringToBool(this.getAttribute(attributes.UPPERCASE));
  }

  /**
   * Sets the text to all lower case
   * @param {boolean | string} n If true use lower case
   */
  set lowercase(n: boolean | string) {
    if (stringToBool(n)) {
      this.setAttribute(attributes.LOWERCASE, '');
      this.input?.classList.add('is-lowercase');
      return;
    }
    this.removeAttribute(attributes.LOWERCASE);
    this.input?.classList.remove('is-lowercase');
  }

  /**
   * Get the lowercase state
   * @returns {boolean | string} true if lowercase is enabled
   */
  get lowercase(): boolean | string {
    return stringToBool(this.getAttribute(attributes.LOWERCASE));
  }

  /**
   * Sets the margin-block-end spacing token on the inner .ids-input container.
   * Accepts token shorthand keys (e.g. 'sm', 'lg') that map to IDS spacing CSS variables.
   * Sets the host attribute (web component usage) and a CSS class on the inner container
   * (standalone CSS usage). Spacing is applied entirely through SCSS — no inline styles are used.
   * @param {MarginBlockEndKey | null} value spacing token key, or null to remove
   */
  set marginBlockEnd(value: MarginBlockEndKey | null) {
    const container = this.container;
    // Remove any previously applied margin-block-end class
    const prev = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (prev) container?.classList.remove(`margin-block-end-${prev}`);

    if (value && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, value)) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
      container?.classList.add(`margin-block-end-${value}`);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
    }
  }

  /**
   * Gets the margin-block-end token key
   * @returns {MarginBlockEndKey | null} the current spacing token key, or null if not set
   */
  get marginBlockEnd(): MarginBlockEndKey | null {
    const val = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (val && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, val)) return val;
    return null;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean | string} n true or false
   */
  set noMargins(n: boolean | string) {
    if (stringToBool(n)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      this.container?.classList.add('no-margins');
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    this.container?.classList.remove('no-margins');
  }

  /**
   * Get the no margins state
   * @returns {boolean | string} true if margins are disabled
   */
  get noMargins(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Sets the inner padding (inline-start and end)
   * @param {string} n string value for the padding
   */
  set padding(n: string | number) {
    if (n) {
      this.setAttribute(attributes.PADDING, String(n));
      this.input?.style.setProperty('padding-inline-start', `${n}px`);
      this.input?.style.setProperty('padding-inline-end', `${n}px`);
      return;
    }
    this.removeAttribute(attributes.PADDING);
    this.input?.style.setProperty('padding-inline-start', ``);
    this.input?.style.setProperty('padding-inline-end', ``);
  }

  /**
   * Get the inner padding
   * @returns {string | number} the padding value
   */
  get padding(): string | number {
    return this.getAttribute(attributes.PADDING) || '';
  }

  /**
   * Get the format attribute
   * @returns {string | null} the date format
   */
  get format(): string | null {
    return this.getAttribute(attributes.FORMAT);
  }

  /**
   * Overrides the standard "blur" behavior to instead tell the inner HTMLInput element to blur.
   */
  blur(): void {
    this.input?.blur();
  }

  /**
   * Overrides the standard "focus" behavior to instead pass focus to the inner HTMLInput element.
   */
  focus(): void {
    this.input?.focus();
  }

  /**
   * Set how content overflows; can specify 'ellipsis', or undefined or 'none'
   * @param {string | null} value how content is overflow
   */
  set overflow(value: string | null) {
    const isEllipsis = value === 'ellipsis';

    if (isEllipsis) {
      this.container?.classList.add('ellipsis');
      this.setAttribute('overflow', 'ellipsis');
    } else {
      this.container?.classList.remove('ellipsis');
      this.removeAttribute('overflow');
    }
  }

  /**
   * Get how content overflows
   * @returns {string | null} the overflow setting
   */
  get overflow(): string | null {
    return this.getAttribute('overflow');
  }

  /**
   *
   * @returns {boolean} true if the input or its container is overflowing
   */
  canTooltipShow(): boolean {
    if (this.disableTooltip) {
      return false;
    }

    if (isValidBooleanValue(this.tooltip) && stringToBool(this.tooltip) === false) return false;

    if (!this.overflow && this.tooltip === 'true') {
      this.overflow = 'ellipsis'; // makes it more backwards compatible
    }

    if (this.overflow !== 'ellipsis') return true;

    if (checkOverflow(this.input)) {
      this.#updateTooltip();
      return true;
    }

    return false;
  }

  /**
   * Target the input element for tooltip positioning
   * @returns {HTMLElement} The input element for proper tooltip alignment
   */
  onTooltipTargetDetection(): HTMLElement {
    return this.input || this;
  }

  /**
   * Handle label break changes
   * @param {string|null} newValue The new label break value
   * @param {string|null} oldValue The old label break value
   */
  onLabelBreakChange(newValue: string | null, oldValue: string | null): void {
    const container = this.container;
    if (!container) return;

    // Remove old classes
    if (oldValue) {
      container.classList.remove(`label-break-${oldValue}`);
    }

    // Add new classes
    if (newValue && newValue !== 'shrink-value') {
      container.classList.add(`label-break-${newValue}`);
    }
  }

  /**
   * Handle label-alignment changes — propagates the CSS custom property to the inner container
   * @returns {void}
   */
  onLabelAlignmentChange(): void {
    const container = this.container;
    if (!container) return;
    if (this.labelAlignment) {
      container.style.setProperty('--ids-label-alignment', this.labelAlignment);
    } else {
      container.style.removeProperty('--ids-label-alignment');
    }
  }

  /**
   * Gets the label container element from the shadow DOM
   * NOTE: Overrides the mixin implementation to return the input-specific container
   * @returns {HTMLElement|null} The .ids-input container element, or null if not found
   */
  override labelContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector('.ids-input') || null;
  }

  /**
   * Push field-height/compact to the trigger-field element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    const showHideButtonElem = this.container?.querySelector('.show-hide-password');
    if (val) {
      const attr = val === 'compact' ? { name: 'compact', val: 'true' } : this.getShowHideButtonHeight(val);
      showHideButtonElem?.setAttribute(attr.name, attr.val);
    } else {
      showHideButtonElem?.removeAttribute('compact');
      showHideButtonElem?.removeAttribute('height');
      showHideButtonElem?.removeAttribute('field-height');
    }
  }

  /**
   * Get the attribute name and value for the show/hide password button based on field height
   * @param {string} fieldHeight The current field height setting
   * @returns {{ name: string, val: string }} The attribute name and value to apply to the button
   */
  getShowHideButtonHeight(fieldHeight: string) {
    if (SIZES.xs === fieldHeight) {
      return { name: 'compact', val: 'true' };
    }

    if (SIZES.sm === fieldHeight) {
      return { name: 'height', val: '26px' };
    }

    return { name: 'height', val: '34px' };
  }
}
